import { useMemo } from 'react'
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useWorkflowBuilderState } from './WorkflowBuilderState'
import type { WorkflowConfigValidationView } from './useWorkflowConfigValidation'

/**
 * Top-of-canvas validation badge. Shows one of three states:
 *
 *   - **Pending** (debounce window): "Validating…" with a spinner.
 *   - **Valid**: "Valid workflow" with a check.
 *   - **Invalid**: a summary of error counts, where each clickable count
 *     focuses the canvas on the first offending step (selects it, which
 *     opens the properties panel on the broken step).
 *
 * Per the workflow-builder capability spec, clicking an error count
 * focuses the canvas on the offending node/edge. We map "click error"
 * → `selectStep(id)` for the first step in `errorsByStepId`. Top-level
 * errors (no-path-to-terminal, version) don't have a per-step focus
 * target, so they list inline below the badge.
 *
 * The badge reads validation state from the shared context (where
 * `LiveWorkflowValidationInjector` writes it after running the hook
 * outside the state provider — same separation pattern as the
 * form-builder).
 */
export function ValidationBadge({ validation }: { validation: WorkflowConfigValidationView }) {
	const { selectStep } = useWorkflowBuilderState()

	const stepErrorEntries = useMemo(
		() => Object.entries(validation.errorsByStepId).filter(([, msgs]) => msgs.length > 0),
		[validation.errorsByStepId],
	)
	const totalStepErrorCount = stepErrorEntries.reduce((acc, [, msgs]) => acc + msgs.length, 0)

	if (validation.isPending) {
		return (
			<div
				role="status"
				aria-label="Validation pending"
				data-testid="validation-badge"
				className="flex items-center gap-2 rounded-md border bg-card px-3 py-2 text-sm text-muted-foreground"
			>
				<Loader2 className="size-4 animate-spin" />
				<span>Validating…</span>
			</div>
		)
	}

	if (!validation.hasErrors && validation.topLevelErrors.length === 0) {
		return (
			<div
				role="status"
				aria-label="Valid workflow"
				data-testid="validation-badge"
				className="flex items-center gap-2 rounded-md border border-emerald-500/40 bg-emerald-50 px-3 py-2 text-sm text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400"
			>
				<CheckCircle2 className="size-4" />
				<span className="font-medium">Valid workflow</span>
			</div>
		)
	}

	return (
		<div
			role="alert"
			aria-label="Validation errors"
			data-testid="validation-badge"
			className="space-y-2 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-sm text-destructive"
		>
			<div className="flex flex-wrap items-center gap-2">
				<AlertCircle className="size-4 shrink-0" />
				<span className="font-medium">Workflow has validation errors</span>
				{totalStepErrorCount > 0 ? (
					<Button
						variant="ghost"
						size="sm"
						onClick={() => {
							const firstId = stepErrorEntries[0]?.[0]
							if (firstId) selectStep(firstId)
						}}
						aria-label={`Focus on first step with errors`}
						data-testid="validation-badge-focus-step"
						className="h-auto px-2 py-1 text-xs text-destructive hover:bg-destructive/10"
					>
						{totalStepErrorCount} step error{totalStepErrorCount === 1 ? '' : 's'} — focus
					</Button>
				) : null}
				{validation.topLevelErrors.length > 0 ? (
					<span className="rounded bg-destructive/10 px-2 py-1 text-xs">
						{validation.topLevelErrors.length} graph error{validation.topLevelErrors.length === 1 ? '' : 's'}
					</span>
				) : null}
			</div>
			{validation.topLevelErrors.length > 0 ? (
				<ul className="space-y-1 pl-2 text-xs">
					{validation.topLevelErrors.map((msg, i) => (
						<li key={i} data-testid="validation-badge-top-level-error">
							{msg}
						</li>
					))}
				</ul>
			) : null}
		</div>
	)
}
